import {
    MonthlySettlementCostSplitDto,
    ChildId,
    ChildSettlementId,
    CostAllocationId,
    SummaryInvoiceDataDto,
    CostAllocationRefersTo,
} from 'types';
import { DetailsColumn, DetailsFrameBase, DetailsRow } from 'components/atoms';
import { commonTexts, settlementTexts } from 'consts/text';
import styled, { css } from 'styled-components';
import { AppCardList, RemoveModal } from 'components/organisms';
import { IconEdit, IconTrash } from '@tabler/icons-react';
import { Option } from 'components/atoms/AppTableAndListCommon/OptionsButton/OptionsButton';
import { AppCardListItem } from 'components/atoms/CardList/AppListCardItem/AppCardListItem';
import { useState, useMemo } from 'react';
import { useAppQuery } from 'services/reactQuery/useAppQuery';

import { payerMapper } from 'consts/settlementChildren/payerMapper';
import { AddCostSplitModal } from 'components/templates/settlement/AddEditCostSplitModal/AddCostSplitModal/AddCostSplitModal';
import { EditCostSplitModal } from 'components/templates/settlement/AddEditCostSplitModal/EditCostSplitModal/EditCostSplitModal';
import { referToMapper } from 'consts/settlementChildren/referToMapper';
import { useIsMobile } from 'hooks/useIsMobile/useIsMobile';
import { useAppMutation } from '../../../../services/reactQuery/useAppMutation';
import StaffHTTPService from '../../../../services/HTTPService/staff/StaffHTTPService';
import { useSimpleNotification } from '../../../../hooks/useSimpleNotification/useSimpleNotification';
import { PriceDisplay } from '../../../molecules/Table/PriceDisplay/PriceDisplay';
import { AdditionalCostInvoice } from '../ChildSettlementDetails/AdditionalCostList/AdditionalCostItem/AdditionalCostDetails/AdditionalCostInvoice';

export const ItemContainer = styled.div`
    margin-block-start: 32px;
`;

const StyledDetailsFrameBase = styled(DetailsFrameBase)`
    ${({ theme }) => css`
        @media (${theme.sizes.mediaQueries.mobileMax}) {
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            gap: ${theme.sizes.marginNormal}px;
        }
    `}
`;

type Props = {
    title: string;
    isCostSplitModificationDisabled?: boolean;
    childId?: ChildId;
    period?: string;
    correctionId?: string;
};

export const ChildSettlementCostSplitList = ({
    title,
    isCostSplitModificationDisabled,
    childId,
    period,
    correctionId,
}: Props) => {
    const [openAddCostSplitModal, setOpenAddCostSplitModal] = useState(false);
    const [editCostSplitId, setEditCostSplitId] = useState<CostAllocationId>();
    const {
        data: childSettlement,
        isLoading,
        isFetching,
    } = useAppQuery(
        'CHILD_SETTLEMENT',
        [childId, period],
        () =>
            StaffHTTPService.settlements.getChildSettlement({
                childId: childId as ChildId,
                period,
            }),
        {},
    );

    const costSplits = useMemo(() => {
        if (correctionId) {
            const correction = childSettlement?.corrections?.find(
                (corr) => corr.id === correctionId,
            );
            return correction?.costSplits || [];
        }
        return childSettlement?.monthlySettlementDetailsDto.costSplits || [];
    }, [childSettlement, correctionId]);
    const { displaySuccess, displayError } = useSimpleNotification();

    const [removeObject, setRemoveObject] = useState<{
        removeTarget: 'costAllocation' | 'splitPayment';
        removeId: ChildSettlementId | CostAllocationId | string;
    } | null>(null);

    const isMobile = useIsMobile();

    const { mutate: removeCostAllocation, isLoading: isRemoveCostAllocationLoading } =
        useAppMutation(StaffHTTPService.settlements.removeCostAllocation, {
            key: ['REMOVE_COST_ALLOCATION'],
            onSuccess: () => {
                displaySuccess(settlementTexts.children.costAllocation.removeSuccess);
                setRemoveObject(null);
            },
            onError: () => {
                displayError(commonTexts.errorMessages.unknown);
            },
            invalidateQueryKeys: [
                ['COST_ALLOCATION'],
                ['CHILD_SETTLEMENT', childId],
                ['CHILD_SETTLEMENT_COST_SPLIT', childId],
                ['CHILD_SETTLEMENT', childId, period],
            ],
        });

    const removeTitle =
        removeObject?.removeTarget === 'costAllocation'
            ? settlementTexts.children.costAllocationRemove
            : settlementTexts.children.paymentRemove;

    const monthlySettlementId = useMemo(
        () => childSettlement?.monthlySettlementDetailsDto.id,
        [childSettlement],
    );

    const onRemoveCancel = () => {
        setRemoveObject(null);
    };

    const onRemoveOk = () => {
        if (removeObject?.removeTarget === 'costAllocation' && removeObject?.removeId) {
            removeCostAllocation({ costAllocationId: removeObject.removeId as CostAllocationId });
        }
    };

    const onDeleteCostAllocation = (costAllocationId: string) => {
        setRemoveObject({
            removeTarget: 'costAllocation',
            removeId: costAllocationId,
        });
    };

    const navigateToAddCostAllocation = () => {
        setOpenAddCostSplitModal(true);
    };

    const navigateToEditCostAllocation = (costAllocationId: CostAllocationId) => {
        setEditCostSplitId(costAllocationId);
    };

    const handleDeleteCostAllocation = (costAllocationId: string) => {
        onDeleteCostAllocation?.(costAllocationId);
    };

    const costAllocationOptions: Option<MonthlySettlementCostSplitDto>[] = [
        {
            label: settlementTexts.children.costAllocation.editButtonLabel,
            Icon: <IconEdit size={20} />,
            onClick: (record) => navigateToEditCostAllocation(record.id as CostAllocationId),
        },
        {
            label: settlementTexts.children.costAllocation.deleteButtonLabel,
            Icon: <IconTrash size={20} />,
            onClick: (record) => handleDeleteCostAllocation(record.id),
        },
    ];

    if (isLoading || isFetching) {
        return null;
    }

    return (
        <>
            <RemoveModal
                open={!!removeObject}
                title={removeTitle}
                onOk={onRemoveOk}
                onCancel={onRemoveCancel}
                confirmActionLoading={isRemoveCostAllocationLoading}
            />
            <ItemContainer>
                <StyledDetailsFrameBase hideHeader={isMobile}>
                    <AppCardList
                        marginTop={-32}
                        title={title}
                        isHiddenAddButton={isCostSplitModificationDisabled}
                        addButtonLabel={settlementTexts.children.costAllocation.addButtonLabel}
                        onAddButtonClick={navigateToAddCostAllocation}
                        dataSource={costSplits}
                        emptyInfo={settlementTexts.children.costAllocation.emptyInfo}
                        renderItem={(record, index) => (
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: isMobile ? 'column' : 'row',
                                    gap: 16,
                                    alignItems: 'stretch',
                                    width: '100%',
                                }}
                            >
                                <div style={{ flex: 1 }}>
                                    <AppCardListItem
                                        key={record.id}
                                        index={index}
                                        listItem={record}
                                        options={
                                            !isCostSplitModificationDisabled
                                                ? costAllocationOptions
                                                : undefined
                                        }
                                    >
                                        <DetailsRow>
                                            <DetailsColumn
                                                title={
                                                    settlementTexts.children.costSplit
                                                        .institutionName
                                                }
                                                span={16}
                                            >
                                                {record.foundationName}
                                            </DetailsColumn>
                                        </DetailsRow>

                                        <DetailsRow>
                                            <DetailsColumn
                                                title={settlementTexts.children.costSplit.nip}
                                                span={6}
                                            >
                                                {record.nip}
                                            </DetailsColumn>
                                            <DetailsColumn
                                                title={
                                                    settlementTexts.children.costSplit.finalAmount
                                                }
                                                span={6}
                                            >
                                                {record.finalGrossAmount ? (
                                                    <PriceDisplay value={record.finalGrossAmount} />
                                                ) : (
                                                    '-'
                                                )}
                                            </DetailsColumn>
                                            <DetailsColumn
                                                title={
                                                    settlementTexts.children.costSplit
                                                        .percentageAmount
                                                }
                                                span={6}
                                            >
                                                {record.percentageAmount
                                                    ? `${record.percentageAmount} %`
                                                    : '-'}
                                            </DetailsColumn>
                                            <DetailsColumn
                                                title={settlementTexts.children.costSplit.payerType}
                                                span={6}
                                            >
                                                {payerMapper[record.payerType]}
                                            </DetailsColumn>
                                        </DetailsRow>

                                        <DetailsRow>
                                            <DetailsColumn
                                                title={settlementTexts.children.costSplit.refersTo}
                                                span={6}
                                            >
                                                {
                                                    referToMapper[
                                                        record.costCategory as CostAllocationRefersTo
                                                    ]
                                                }
                                            </DetailsColumn>

                                            <DetailsColumn
                                                title={
                                                    settlementTexts.children.costSplit.invoiceNotes
                                                }
                                                span={12}
                                            >
                                                {record.invoiceNotes}
                                            </DetailsColumn>
                                        </DetailsRow>
                                    </AppCardListItem>
                                </div>

                                {(
                                    record as MonthlySettlementCostSplitDto & {
                                        invoiceData?: SummaryInvoiceDataDto;
                                    }
                                ).invoiceData && (
                                    <div
                                        style={{
                                            width: isMobile ? '100%' : 320,
                                            flexShrink: 0,
                                            display: 'flex',
                                        }}
                                    >
                                        <AdditionalCostInvoice
                                            invoiceData={
                                                (
                                                    record as MonthlySettlementCostSplitDto & {
                                                        invoiceData: SummaryInvoiceDataDto;
                                                    }
                                                ).invoiceData!
                                            }
                                            title="Rozliczenie"
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    />
                </StyledDetailsFrameBase>
            </ItemContainer>
            <AddCostSplitModal
                type="monthly-settlement"
                isOpen={openAddCostSplitModal}
                onClose={() => setOpenAddCostSplitModal(false)}
                childId={childId}
                monthlySettlementId={monthlySettlementId}
                correctionId={correctionId}
            />
            {editCostSplitId && (
                <EditCostSplitModal
                    type="monthly-settlement"
                    isOpen={!!editCostSplitId}
                    onClose={() => setEditCostSplitId(undefined)}
                    childId={childId}
                    monthlySettlementId={monthlySettlementId}
                    costAllocationId={editCostSplitId}
                    correctionId={correctionId}
                />
            )}
        </>
    );
};
